import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WalletCardsIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { trackSubmitApplication } from '../../utils/metaPixel';
import { getTrafficIntent } from '../../utils/trafficIntent';

// Official link provided by Cherry. `m=97021` is the practice/merchant id and
// `utm_source=practice` is Cherry's own required tagging — neither may change.
// utm_medium is `website` (Cherry's snippet used `textmessage`) so applications
// from this landing aren't attributed to the SMS campaign.
const CHERRY_BASE_URL = 'https://pay.withcherry.com/lipo-laser';
const CHERRY_REQUIRED_PARAMS = {
  utm_source: 'practice',
  utm_medium: 'website',
  m: '97021'
};

/**
 * Appends the visitor's campaign attribution to Cherry's application URL so
 * financing applications can be traced back to the ad that produced them.
 *
 * Cherry's own required params are never overwritten. Campaign values are
 * namespaced (`campaign`, `creative`, ...) to avoid colliding with the
 * utm_source / utm_medium Cherry reserves for itself.
 */
function buildCherryUrl(): string {
  const intent = getTrafficIntent();
  const params = new URLSearchParams(CHERRY_REQUIRED_PARAMS);
  params.set('traffic_intent', intent.isHighIntent ? 'high_intent' : 'standard');
  if (intent.utmCampaign) params.set('campaign', intent.utmCampaign);
  if (intent.utmContent) params.set('creative', intent.utmContent);
  if (intent.utmTerm) params.set('term', intent.utmTerm);
  if (intent.flow) params.set('flow', intent.flow);
  if (intent.utmSource) params.set('origin', intent.utmSource);
  return `${CHERRY_BASE_URL}?${params.toString()}`;
}

/**
 * Compact Cherry financing entry point.
 *
 * Deliberately restrained: financing is a SECONDARY action. It stays out of
 * the way and hides completely once the visitor reaches the qualification
 * wizard / contact form, so nothing competes with the primary goal — filling
 * out the consultation form.
 *
 * Opens Cherry's hosted application directly instead of loading their widget
 * script, which repeatedly crashed the page with a cross-origin script error.
 */
export function CherryFinancing() {
  const {
    lang
  } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const evaluate = () => {
      const contact = document.getElementById('contacto');
      const scrolledEnough = window.scrollY > 700;
      const contactReached = contact ? window.scrollY + window.innerHeight > contact.offsetTop + 120 : false;
      setIsVisible(scrolledEnough && !contactReached);
    };
    evaluate();
    window.addEventListener('scroll', evaluate, {
      passive: true
    });
    window.addEventListener('resize', evaluate);
    return () => {
      window.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
    };
  }, []);
  const copy = lang === 'es' ? {
    label: 'Financiamiento',
    aria: 'Aplicar a financiamiento con Cherry — se abre en una pestaña nueva'
  } : {
    label: 'Financing',
    aria: 'Apply for financing with Cherry — opens in a new tab'
  };
  return <AnimatePresence data-id="element-62" data-mp="3i7o3zmy65wf0">
      {isVisible && <motion.a href={buildCherryUrl()} target="_blank" rel="noopener noreferrer" onClick={() => trackSubmitApplication({
      provider: 'cherry',
      slug: 'lipo-laser'
    })} aria-label={copy.aria} initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 16
    }} transition={{
      duration: 0.4,
      ease: [0.33, 1, 0.68, 1]
    }} className="fixed bottom-28 right-4 z-30 inline-flex items-center gap-2 rounded-full border border-brand-dark/10 bg-brand-cream/95 px-4 py-2.5 shadow-lg shadow-black/10 backdrop-blur-md transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 lg:bottom-6 lg:right-6" data-id="element-63" data-mp="3df3akmj6dlc0">
          <WalletCardsIcon className="h-3.5 w-3.5 text-brand-gold" strokeWidth={1.8} data-id="element-64" data-mp="16uo4kao93lmv" />
          <span className="text-[10px] font-medium tracking-[0.15em] uppercase text-brand-dark/70" data-id="element-65" data-mp="a5q5whddr3kf">
            {copy.label}
          </span>
        </motion.a>}
    </AnimatePresence>;
}